import { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Search, X, ArrowLeft, User, Hash, ArrowRight, Loader2, Sparkles, Clock } from 'lucide-react';
import { useDebounce } from '../../../hooks/use-debounce';
import { useTypeaheadSearch } from '../use-unified-search';
import { StarBadge } from '../../../components/shared/star-badge';

const SEARCH_HISTORY_KEY = 'candle_search_history';
const MAX_HISTORY_ITEMS = 3;

interface MobileSearchOverlayProps {
    onClose: () => void;
    onNavigate: (path: string) => void;
}

export function MobileSearchOverlay({ onClose, onNavigate }: MobileSearchOverlayProps) {
    const { t } = useTranslation();
    const [query, setQuery] = useState('');
    const [searchHistory, setSearchHistory] = useState<string[]>([]);
    const inputRef = useRef<HTMLInputElement>(null);
    const debouncedQuery = useDebounce(query, 300);
    const { data, isLoading } = useTypeaheadSearch(debouncedQuery, debouncedQuery.length >= 3);

    const hasResults = data && (data.pages.length > 0 || data.tags.length > 0);
    const queryTooShort = query.length < 3;
    const showHistory = queryTooShort && searchHistory.length > 0;

    // Auto-focus input on open
    useEffect(() => {
        requestAnimationFrame(() => {
            inputRef.current?.focus();
        });
    }, []);

    // Lock body scroll
    useEffect(() => {
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = '';
        };
    }, []);

    // Load search history from localStorage on mount
    useEffect(() => {
        try {
            const stored = localStorage.getItem(SEARCH_HISTORY_KEY);
            if (stored) {
                const history = JSON.parse(stored);
                setSearchHistory(Array.isArray(history) ? history : []);
            }
        } catch (error) {

        }
    }, []);

    // Save search to history
    const saveToHistory = useCallback((searchQuery: string) => {
        const trimmed = searchQuery.trim();
        if (trimmed.length < 3) return;

        try {
            setSearchHistory((prev) => {
                // Remove if already exists, then add to front
                const filtered = prev.filter(item => item !== trimmed);
                const updated = [trimmed, ...filtered].slice(0, MAX_HISTORY_ITEMS);
                localStorage.setItem(SEARCH_HISTORY_KEY, JSON.stringify(updated));
                return updated;
            });
        } catch (error) {

        }
    }, []);

    const handleViewAll = () => {
        if (query.trim().length >= 3) {
            saveToHistory(query.trim());
            onNavigate(`/search?q=${encodeURIComponent(query.trim())}`);
        }
    };

    const handleHistorySelect = (historyQuery: string) => {
        setQuery(historyQuery);
        saveToHistory(historyQuery);
        onNavigate(`/search?q=${encodeURIComponent(historyQuery)}`);
    };

    const handleResultClick = (path: string) => {
        if (query.trim().length >= 3) {
            saveToHistory(query.trim());
        }
        onNavigate(path);
    };

    return createPortal(
        <div className="mobile-search-overlay" role="dialog" aria-modal="true" aria-label={t('unifiedSearch.openSearch')}>
            {/* Header */}
            <div className="mobile-search-header">
                <button onClick={onClose} className="mobile-search-back" aria-label={t('common.cancel')}>
                    <ArrowLeft className="h-5 w-5" />
                </button>
                <div className="mobile-search-input-wrap">
                    <Search className="h-4 w-4 text-muted-foreground" />
                    <input
                        ref={inputRef}
                        type="text"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder={t('unifiedSearch.placeholder')}
                        className="mobile-search-input"
                        autoComplete="off"
                        spellCheck={false}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') handleViewAll();
                        }}
                    />
                    {query.length > 0 && (
                        <button onClick={() => { setQuery(''); inputRef.current?.focus(); }} className="mobile-search-clear">
                            <X className="h-4 w-4" />
                        </button>
                    )}
                </div>
            </div>

            {/* Results */}
            <div className="mobile-search-results">
                {/* Search History */}
                {showHistory && (
                    <div className="typeahead-section">
                        <div className="typeahead-section-header">
                            <Clock className="h-3.5 w-3.5" />
                            <span>{t('unifiedSearch.recentSearches', 'Recent Searches')}</span>
                        </div>
                        {searchHistory.map((historyItem, idx) => (
                            <button
                                key={idx}
                                className="typeahead-item w-full text-left"
                                onClick={() => handleHistorySelect(historyItem)}
                            >
                                <div className="typeahead-item-tag-icon">
                                    <Clock className="h-4 w-4" />
                                </div>
                                <div className="typeahead-item-content">
                                    <span className="typeahead-item-name">{historyItem}</span>
                                </div>
                            </button>
                        ))}
                    </div>
                )}

                {/* Too short hint */}
                {query.length > 0 && query.length < 3 && !showHistory && (
                    <div className="typeahead-hint">
                        <Sparkles className="h-4 w-4 text-primary/60" />
                        <span>{t('unifiedSearch.minCharsHint')}</span>
                    </div>
                )}

                {/* Loading */}
                {query.length >= 3 && isLoading && (
                    <div className="typeahead-loading">
                        <Loader2 className="h-4 w-4 animate-spin text-primary" />
                        <span>{t('unifiedSearch.searching')}</span>
                    </div>
                )}

                {/* No results */}
                {query.length >= 3 && !isLoading && !hasResults && debouncedQuery.length >= 3 && (
                    <div className="typeahead-empty">
                        <span>{t('unifiedSearch.noResults')}</span>
                        <span className="typeahead-empty-sub">{t('unifiedSearch.tryDifferent')}</span>
                    </div>
                )}

                {/* Pages */}
                {data && data.pages.length > 0 && (
                    <div className="typeahead-section">
                        <div className="typeahead-section-header">
                            <User className="h-3.5 w-3.5" />
                            <span>{t('unifiedSearch.memorialPages')}</span>
                            <span className="typeahead-count">{data.total_pages}</span>
                        </div>
                        {data.pages.map((page) => (
                            <button
                                key={page.page_id}
                                className="typeahead-item w-full text-left"
                                onClick={() => handleResultClick(`/memorial/${page.slug || page.page_id}`)}
                            >
                                <div className="relative flex-shrink-0">
                                    <div className="typeahead-item-avatar">
                                        {page.image_url ? (
                                            <img src={page.image_url} alt={page.fullname} className="h-full w-full object-cover" />
                                        ) : (
                                            <span className="typeahead-item-avatar-fallback">{page.fullname[0]?.toUpperCase()}</span>
                                        )}
                                    </div>
                                    <StarBadge starColor={page.star_color} size="xs" />
                                </div>
                                <div className="typeahead-item-content">
                                    <span className="typeahead-item-name">{page.fullname}</span>
                                    <div className="typeahead-item-meta">
                                        {page.country_name && <span>{page.country_name}</span>}
                                        {page.place_of_birth && (
                                            <>
                                                <span className="typeahead-item-separator">·</span>
                                                <span>{page.place_of_birth}</span>
                                            </>
                                        )}
                                    </div>
                                </div>
                                {page.is_private && (
                                    <span className="typeahead-privacy-badge">
                                        <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                        </svg>
                                    </span>
                                )}
                            </button>
                        ))}
                    </div>
                )}

                {/* Tags */}
                {data && data.tags.length > 0 && (
                    <div className="typeahead-section">
                        <div className="typeahead-section-header">
                            <Hash className="h-3.5 w-3.5" />
                            <span>{t('unifiedSearch.aTags')}</span>
                            <span className="typeahead-count">{data.total_tags}</span>
                        </div>
                        {data.tags.map((tag) => (
                            <button
                                key={tag.tag_id}
                                className="typeahead-item w-full text-left"
                                onClick={() => handleResultClick(`/atag/${tag.slug || tag.tag_id}`)}
                            >
                                <div className="typeahead-item-tag-icon">
                                    <Hash className="h-4 w-4" />
                                </div>
                                <div className="typeahead-item-content">
                                    <span className="typeahead-item-name">{tag.display_name || tag.tag_name}</span>
                                    <span className="text-[10px] text-muted-foreground font-mono">@{tag.tag_name}</span>
                                    <div className="typeahead-item-meta">
                                        {tag.no_tagged_pages != null && (
                                            <span>{tag.no_tagged_pages} {t('unifiedSearch.linkedPages')}</span>
                                        )}
                                        {tag.access_type === 'managed' && (
                                            <>
                                                <span className="typeahead-item-separator">·</span>
                                                <span className="typeahead-managed-badge">{t('unifiedSearch.managed')}</span>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </button>
                        ))}
                    </div>
                )}

                {/* View all */}
                {hasResults && (
                    <button className="typeahead-view-all" onClick={handleViewAll}>
                        <span>{t('unifiedSearch.viewAllResults')}</span>
                        <ArrowRight className="h-4 w-4" />
                    </button>
                )}
            </div>
        </div>,
        document.body
    );
}
